// src/components/Admin/Settings/Integration_Setting/SmsTemplateModal.jsx
import React, { useState, useMemo } from "react";
import { X, Save, MessageSquare } from "lucide-react";

export default function SmsTemplateModal({
  item,
  onSave,
  onClose,
  eventOptions = [],
}) {
  const [formData, setFormData] = useState({
    event: item?.event || "",
    smsTemplate: item?.smsTemplate || "",
    templatID: item?.templatID || "",
    senderID: item?.senderID || "",
    remarks: item?.remarks || "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isValid = useMemo(() => {
    return (
      formData.event &&
      formData.smsTemplate.trim() &&
      formData.templatID.trim() &&
      formData.senderID.trim() &&
      formData.remarks.trim()
    );
  }, [formData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
        {/* Header - Teal themed */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {item ? "Edit SMS Template" : "New SMS Template"}
              </h2>
              <p className="text-xs text-teal-100">
                Configure SMS template settings
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Select Event */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Select Event<span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  value={formData.event}
                  onChange={(e) => handleChange("event", e.target.value)}
                >
                  <option value="">Select Event</option>
                  {eventOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* SMS Template Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  SMS Template Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.smsTemplate}
                  onChange={(e) => handleChange("smsTemplate", e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Enter SMS template name"
                />
              </div>

              {/* DLT Template ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  DLT Template ID<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.templatID}
                  onChange={(e) => handleChange("templatID", e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Enter DLT Template ID"
                />
              </div>

              {/* Sender ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Sender ID<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.senderID}
                  onChange={(e) => handleChange("senderID", e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Enter Sender ID"
                />
              </div>
            </div>

            {/* Text Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Text Message<span className="text-red-500">*</span>
              </label>
              <textarea
                rows={4}
                value={formData.remarks}
                onChange={(e) => handleChange("remarks", e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                placeholder="Enter message text"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium text-white flex items-center gap-2 transition-all ${
                isValid
                  ? "bg-teal-600 hover:bg-teal-700"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
            >
              <Save className="w-4 h-4" />
              {item ? "Update" : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
